import { isNil, Nillable } from '@web/utils';
import {
  TableCollectionAction,
  TableColumn,
  TableConfig,
  TableFilterConfig,
  TableItemAction,
} from '../models';
import { TableDataKey } from '../types/table-data-key';
import { TableDataSource } from '../providers/table-data.source';
import { BodyRowCssClassFactory } from '../types/body-row-css-class-factory';
import { BodyRowSelectionDisabledFactory } from '../types/body-row-selection-disabled-factory';
import { TableColumnBuilder } from './table-column.builder';
import { TableFilterBuilder } from './table-filter-builder';
import { ActionButton } from '../../action/models/action-button';
import { ActionButtonBuilder } from '../../action/builders/action-button.builder';
import { RequiredMethodCallException } from '@web/common';
import { TableCollectionActionBuilder } from './table-collection-action.builder';
import { TableItemActionBuilder } from './table-item-action.builder';

export class TableBuilder<
  Item,
  Filter = never,
  ItemAction = never,
  CollectionAction = never,
  Action = never,
> {
  private _columns: Nillable<TableColumn<Item, Filter>[]>;
  private _actions: Nillable<ActionButton<Action>[]>;
  private _itemActions: Nillable<TableItemAction<Item, ItemAction>[]>;
  private _filters: Nillable<TableFilterConfig<Filter>[]>;
  private _dataKey: Nillable<TableDataKey<Item>>;
  private _dataSource: Nillable<TableDataSource<Item, Filter>>;
  private _bodyRowCssClass: Nillable<BodyRowCssClassFactory<Item>>;
  /* prettier-ignore */
  private _bodyRowSelectionDisabledFn: Nillable<BodyRowSelectionDisabledFactory<Item>>;
  /* prettier-ignore */
  private _collectionActions: Nillable<TableCollectionAction<Item, Filter, CollectionAction>[]>;
  private _filtering: Nillable<boolean>;
  private _pagination: Nillable<boolean>;
  private _preserveSelectionOnFilter: Nillable<boolean>;
  private _reordering: Nillable<boolean>;
  private _topScrollEnabled: Nillable<boolean>;
  private _scrollHeight: Nillable<string>;
  private _responsiveLayout: Nillable<'scroll' | 'stack'>;

  public constructor(
    private readonly columnsBuilder: TableColumnBuilder<Item, Filter>,
    private readonly filtersBuilder: TableFilterBuilder<Filter>,
    private readonly actionBuilder: ActionButtonBuilder<Action>,
    private readonly collectionActionBuilder: TableCollectionActionBuilder<
      Item,
      Filter,
      CollectionAction
    >,
    private readonly itemActionBuilder: TableItemActionBuilder<
      Item,
      ItemAction
    >,
  ) {
    this.reset();
  }

  public reset(): this {
    this._columns = null;
    this._actions = null;
    this._collectionActions = null;
    this._itemActions = null;
    this._filters = null;
    this._dataKey = null;
    this._dataSource = null;
    this._bodyRowCssClass = null;
    this._bodyRowSelectionDisabledFn = null;
    this._filtering = null;
    this._pagination = null;
    this._preserveSelectionOnFilter = null;
    this._reordering = null;
    this._topScrollEnabled = null;
    this._scrollHeight = null;
    this._responsiveLayout = null;

    return this;
  }

  public newInstance(): TableBuilder<
    Item,
    Filter,
    ItemAction,
    CollectionAction,
    Action
  > {
    return new TableBuilder<Item, Filter, ItemAction, CollectionAction, Action>(
      this.columnsBuilder,
      this.filtersBuilder,
      this.actionBuilder,
      this.collectionActionBuilder,
      this.itemActionBuilder,
    );
  }

  public dataKey(dataKey: TableDataKey<Item>): this {
    this._dataKey = dataKey;

    return this;
  }

  public dataSource(dataSource: TableDataSource<Item, Filter>): this {
    this._dataSource = dataSource;

    return this;
  }

  public filters(
    factory: (
      builder: TableFilterBuilder<Filter>,
    ) => TableFilterConfig<Filter>[],
  ): this {
    if (isNil(this._filters)) {
      this._filters = [];
    }

    this._filters = [...this._filters, ...factory(this.filtersBuilder)];

    return this;
  }

  public columns(
    factory: (
      builder: TableColumnBuilder<Item, Filter>,
    ) => TableColumn<Item, Filter>[],
  ): this {
    if (isNil(this._columns)) {
      this._columns = [];
    }

    this._columns = [...this._columns, ...factory(this.columnsBuilder)];

    return this;
  }

  public actions(
    factory: (builder: ActionButtonBuilder<Action>) => ActionButton<Action>[],
  ): this {
    if (isNil(this._actions)) {
      this._actions = [];
    }

    this._actions = [...this._actions, ...factory(this.actionBuilder)];

    return this;
  }

  public collectionActions(
    factory: (
      builder: TableCollectionActionBuilder<Item, Filter, CollectionAction>,
    ) => TableCollectionAction<Item, Filter, CollectionAction>[],
  ): this {
    if (isNil(this._collectionActions)) {
      this._collectionActions = [];
    }

    this._collectionActions = [
      ...this._collectionActions,
      ...factory(this.collectionActionBuilder),
    ];

    return this;
  }

  public itemActions(
    factory: (
      builder: TableItemActionBuilder<Item, ItemAction>,
    ) => TableItemAction<Item, ItemAction>[],
  ): this {
    if (isNil(this._itemActions)) {
      this._itemActions = [];
    }

    this._itemActions = [
      ...this._itemActions,
      ...factory(this.itemActionBuilder),
    ];

    return this;
  }

  public bodyRowCssClassFn(factory: BodyRowCssClassFactory<Item>): this {
    this._bodyRowCssClass = factory;

    return this;
  }

  public bodyRowSelectionDisabledFn(factory: (item: Item) => boolean): this {
    this._bodyRowSelectionDisabledFn = factory;

    return this;
  }

  public filtering(filtering: boolean): this {
    this._filtering = filtering;

    return this;
  }

  public topScrollEnabled(topScrollEnabled: boolean): this {
    this._topScrollEnabled = topScrollEnabled;
    return this;
  }

  public scrollHeight(scrollHeight: string): this {
    this._scrollHeight = scrollHeight;
    return this;
  }


  public responsiveLayout(responsiveLayout: 'scroll' | 'stack'): this {
    this._responsiveLayout = responsiveLayout;
    return this;
  }

  public pagination(pagination: boolean) {
    this._pagination = pagination;

    return this;
  }

  public preserveSelectionOnFilter(preserveSelectionOnFilter: boolean): this {
    this._preserveSelectionOnFilter = preserveSelectionOnFilter;

    return this;
  }

  public reordering(reordering: boolean) {
    this._reordering = reordering;

    return this;
  }

  public build(): TableConfig<
    Item,
    Filter,
    ItemAction,
    CollectionAction,
    Action
  > {
    this.configureDefaultBodyRowSelectionDisabledFn();
    this.configureDefaultFiltering();
    this.configureDefaultPagination();
    this.configureDefaultPreserveSelectionOnFilter();
    this.configureDefaultReordering();
    this.configureDefaultTopScrollEnabled();
    this.configureDefaultScrollHeight();
    this.configureDefaultResponsiveLayout();
    this.requireDataSource();
    this.requireDataKey();

    const table = new TableConfig<
      Item,
      Filter,
      ItemAction,
      CollectionAction,
      Action
    >(
      this._dataKey!,
      this._columns!,
      this._dataSource!,
      this._filters!,
      this._actions!,
      this._collectionActions!,
      this._itemActions!,
      this._bodyRowCssClass!,
      this._bodyRowSelectionDisabledFn!,
      this._filtering!,
      this._pagination!,
      this._preserveSelectionOnFilter!,
      this._reordering!,
      this._topScrollEnabled!,
      this._scrollHeight!,
      this._responsiveLayout!,
    );

    this.reset();

    return table;
  }

  private configureDefaultBodyRowSelectionDisabledFn(): void {
    if (!isNil(this._bodyRowSelectionDisabledFn)) {
      return;
    }

    this.bodyRowSelectionDisabledFn(() => false);
  }

  private configureDefaultFiltering(): void {
    if (!isNil(this._filtering)) {
      return;
    }

    this.filtering(true);
  }

  private configureDefaultPagination(): void {
    if (!isNil(this._pagination)) {
      return;
    }

    this.pagination(true);
  }

  private configureDefaultPreserveSelectionOnFilter(): void {
    if (!isNil(this._preserveSelectionOnFilter)) {
      return;
    }

    this.preserveSelectionOnFilter(false);
  }

  private configureDefaultReordering(): void {
    if (!isNil(this._reordering)) {
      return;
    }

    this.reordering(false);
  }

  private configureDefaultTopScrollEnabled(): void {
    if (!isNil(this._topScrollEnabled)) {
      return;
    }

    this.topScrollEnabled(true);
  }

  private configureDefaultScrollHeight(): void {
    if (!isNil(this._scrollHeight)) {
      return;
    }

    this.scrollHeight('100vh');
  }

  private configureDefaultResponsiveLayout(): void {
    if (!isNil(this._responsiveLayout)) {
      return;
    }

    this.responsiveLayout('scroll');
  }

  private requireDataSource(): void {
    if (!isNil(this._dataSource)) {
      return;
    }

    throw new RequiredMethodCallException(this.dataSource.name);
  }

  private requireDataKey(): void {
    if (!isNil(this._dataKey)) {
      return;
    }

    throw new RequiredMethodCallException(this.dataKey.name);
  }
}
